import {Cell, Row, Worksheet} from 'exceljs';
import {ConvertorFieldTypeToDataType} from 'src/components/data-types/constants';
import {createAttributeRoutePath} from 'src/modules/attributes/attributes-route-planner';
import {COLUMN_CAPTIONS, ColumnTypes} from 'src/modules/attributes/constants';
import {getCriteriaRichText} from 'src/modules/attributes/download/criteria/get-criteria';
import {Filters} from 'src/modules/attributes/typedefs';
import {
    decorateCell,
    decorateLinkCell,
    decorateLinkTableCell,
    decorateSheetTitleCell,
    decorateTableCell,
    decorateTableHeaderCell,
    setCellValue,
} from 'src/modules/downloads/cells/cells';
import {HorizontalAlignment, VerticalAlignment} from 'src/modules/downloads/cells/styles';
import {printLogo} from 'src/modules/downloads/commons/print-logo';
import {printMetadata} from 'src/modules/downloads/commons/print-metadata';
import {setColumnWidth} from 'src/modules/downloads/utils/set-column-width';
import {AttributeRelationInfo} from 'src/openapi/models/AttributeRelationInfo';

const FIRST_ROW_HEIGHT = 30;
const FIRST_ROW = 1;
const TABLE_HEADER_ROW = 9;
const TABLE_HEADER_HEIGHT = 35;
const FIRST_DATA_ROW = 10;
const SHEET_TITLE = 'Attributes';
const LINK_CAPTION = 'View Attributes';
const CRITERIA = 'Criteria:';
const LINK = 'Link:';
const INFO_TOOLTIP_CAPTION = '*';
const EVENT_CODES_LIMIT = 7;
const INFO_TOOLTIP_TEXT = `Some attributes contain more than ${EVENT_CODES_LIMIT} event codes.
To see all event codes and additional information, click More Details.`;
const MORE_DETAILS = 'More Details';

const printCriteria = (worksheet: Worksheet, filters: Filters) => {
    worksheet.mergeCells('B4:D6');
    const cell = worksheet.getCell('B4') as Partial<Cell>;
    const richText = getCriteriaRichText(filters);
    cell.value = {richText};
    cell.alignment = {vertical: VerticalAlignment.TOP, wrapText: true};
};

export const printSheetHeader = (worksheet: Worksheet, userName: string, filters: Filters) => {
    const row = worksheet.getRow(FIRST_ROW) as Partial<Row>;
    row.height = FIRST_ROW_HEIGHT;

    const link = (cell: Partial<Cell>) =>
        cell.value = {text: LINK_CAPTION, hyperlink: window.location.href};

    printLogo(worksheet);
    printCriteria(worksheet, filters);
    printMetadata(worksheet, userName);
    decorateSheetTitleCell(worksheet.getCell('C1'), setCellValue(SHEET_TITLE, HorizontalAlignment.CENTER));
    decorateLinkCell(worksheet.getCell('B3'), link);
    decorateCell(worksheet.getCell('A3'), setCellValue(LINK, HorizontalAlignment.RIGHT));
    decorateCell(worksheet.getCell('A4'), setCellValue(CRITERIA, HorizontalAlignment.RIGHT, VerticalAlignment.TOP));
    decorateCell(worksheet.getCell('A7'), setCellValue(INFO_TOOLTIP_CAPTION, HorizontalAlignment.RIGHT));
    decorateCell(worksheet.getCell('B7'), setCellValue(INFO_TOOLTIP_TEXT));
};

export const printTableDataHeader = (worksheet: Worksheet) => {
    const row = worksheet.getRow(TABLE_HEADER_ROW) as Partial<Row>;
    row.height = TABLE_HEADER_HEIGHT;

    const attribute = COLUMN_CAPTIONS[ColumnTypes.ATTRIBUTE];
    const dataTypes = COLUMN_CAPTIONS[ColumnTypes.DATA_TYPE];
    const codes = `${COLUMN_CAPTIONS[ColumnTypes.EVENT_CODES]} *`;

    decorateTableHeaderCell(worksheet.getCell('A9'), 40, setCellValue(attribute, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('B9'), 50, setCellValue(dataTypes, HorizontalAlignment.CENTER));
    decorateTableHeaderCell(worksheet.getCell('C9'), 50, setCellValue(codes, HorizontalAlignment.CENTER));
    setColumnWidth(worksheet.getCell('E9'), 55);

    worksheet.mergeCells('C9:L9');
};

export const printTableData = (worksheet: Worksheet, attributes: Array<AttributeRelationInfo>) => {
    attributes.forEach((attribute, index) => {
        const rowIndex = FIRST_DATA_ROW + index;
        const row = worksheet.getRow(rowIndex);

        const pathToAttribute = createAttributeRoutePath({attributeName: attribute.name, attributeId: 0});
        const link = (cell: Partial<Cell>) =>
            cell.value = {text: MORE_DETAILS, hyperlink: `${window.location.origin}${pathToAttribute}`};

        const dataTypes = attribute.fieldTypes
            .map(fieldType => ConvertorFieldTypeToDataType[fieldType]).join(', ');
        const limitedEventCodes = attribute.eventCodes.slice(0, EVENT_CODES_LIMIT).flat();
        const eventCodes = limitedEventCodes.map(code => code).join(', ');

        decorateTableCell(row, 1, setCellValue(attribute.name));
        decorateTableCell(row, 2, setCellValue(dataTypes));
        decorateTableCell(row, 3, setCellValue(eventCodes));
        decorateLinkTableCell(row, 11, link);

        worksheet.mergeCells(`C${rowIndex}:J${rowIndex}`);
        worksheet.mergeCells(`K${rowIndex}:L${rowIndex}`);
    });
};
